/**
 * Public self-registration page (route /signup, round7/signup-ui, P4.5-B).
 * NOT wrapped in <ProtectedRoute> — same pattern as /login in main.tsx: a
 * visitor here has no Cognito session at all yet.
 *
 * Flow: plan pick (three tiers x monthly/annual, 30-day trial WITH a card)
 * -> business/contact/email fields -> consent checkbox -> submit ->
 * REDIRECT TO STRIPE CHECKOUT. POST /api/register (registerHandler.ts)
 * returns { tenantId, status: "pending_card", checkoutUrl }, and this page
 * must send the visitor to that URL: the account it just created is inert
 * until a card is submitted, and Checkout is the only way to activate it.
 * There is deliberately no "check your email" stop in between — that would
 * park the user on a dead end mid-signup. The welcome email still goes out
 * (they need it to verify and log in), and Stripe returns them to /welcome,
 * which says so.
 *
 * === Unlimited only, for now (round12/self-serve-unlimited-only) ===
 * Sarah's 2026-09-30 decision: self-serve signup offers the UNLIMITED tier
 * only. The picker below is driven by `SELF_SERVE_TIERS`, a value copy of
 * DockPro-Backend's list of the same name (lib/lambda/utils/
 * stripePriceCatalog.ts) — the backend refuses any other tier with 400
 * TIER_NOT_SELF_SERVE, so widening this list without widening that one
 * only produces a dead option. With a single plan there is nothing to
 * choose, so it is PRE-SELECTED and the legend reads "Your plan"; with
 * more than one, `tier` starts unselected (no default), because that is
 * the one deliberate monetization choice a signup makes and nothing here
 * should nudge it. History: this offered mid/top from round7, and all
 * three tiers from 2026-09-10 when Basic got its own Stripe Prices. Both
 * widenings are one-line edits here and in the backend list.
 *
 * === No priceId, no getBillingPrices() call (correction #2) ===
 * Unlike POST /api/portal/billing/checkout-session (which needs a real
 * Stripe Price id from the AUTHENTICATED GET /api/portal/billing/prices
 * catalog), POST /api/register validates `tier`/`interval` as plain enums
 * and resolves the actual Stripe price SERVER-SIDE — and a not-yet-
 * registered visitor has no idToken to call an authenticated endpoint with
 * anyway. So this page's picker is fully static: it sends `{tier, interval}`
 * and nothing else. `tierLabel`/`intervalLabel`/`TIER_BLURBS` are imported
 * from lib/billing.ts (the same module BillingPage.tsx's plan picker uses)
 * for display-string consistency between the two pickers — see that
 * module's own doc comment on TIER_BLURBS for why it was lifted there
 * rather than kept as BillingPage.tsx's private constant.
 *
 * === Consent (H-3, resolved) ===
 * The three real, already-legally-confirmed Termly documents + the locked
 * `policyVersion` all live in ONE config module, lib/legalConsent.ts — see
 * its own header. `{policyVersion, textShown}` is recorded on submit exactly
 * as registerHandler.ts's RegistrationConsent contract expects (no
 * timestamp — the server stamps that itself at write time).
 *
 * === Field-level validation (house style) ===
 * Sequential, single-message validation matching SettingsPage.tsx's
 * handleSave / LoginPage.tsx's handleNewPassword conventions: check each
 * rule in order, `setSubmitError` + early `return` on the first failure,
 * never a per-field error map. `EMAIL_RX`/`MAX_BUSINESS_NAME_LENGTH`/
 * `MAX_CONTACT_NAME_LENGTH` below MIRROR (this repo has no dependency on
 * DockPro-Backend, so this is a value copy, not an import — same posture as
 * e.g. api.ts's DesignerPresetName) DockPro-Backend's
 * lib/lambda/utils/validation.ts constants of the same name exactly, so a
 * client-side rejection and the server's own rejection agree; the server
 * remains the actual source of truth regardless; if the two drift, this is
 * the file to check.
 *
 * === The 409 case (correction: check the exact shape, don't guess) ===
 * registerHandler.ts's EMAIL_ALREADY_REGISTERED message is already
 * specific, safe, actionable copy — "An account with this email already
 * exists. Try logging in, or use \"Forgot password\" to regain access." —
 * shown VERBATIM (same "reuse the backend's own message for a known code"
 * convention SettingsPage.tsx's designerPreset error handling already
 * established), plus a real `<Link to="/login">` added alongside it so the
 * pointer is an actual clickable affordance, not just prose. The form stays
 * visible/editable underneath (the visitor may have simply mistyped the
 * email) rather than being replaced by a dead end.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { StatusMessage } from "../components/StatusMessage";
import { friendlyErrorMessage } from "../components/friendlyErrorMessage";
import { BrandHeader } from "../components/BrandHeader";
import {
  register, ApiError, type BillingTier, type BillingInterval,
} from "../lib/api";
import { tierLabel, intervalLabel, TIER_BLURBS } from "../lib/billing";
import { CONSENT_DOCUMENTS, CONSENT_POLICY_VERSION, CONSENT_TEXT_SHOWN } from "../lib/legalConsent";
import styles from "./SignupPage.module.css";

// Mirrors DockPro-Backend's lib/lambda/utils/validation.ts exactly — see
// this file's header for why this is a value copy, not an import.
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_BUSINESS_NAME_LENGTH = 200;
const MAX_CONTACT_NAME_LENGTH = 200;

// Mirrors DockPro-Backend's SELF_SERVE_TIERS (lib/lambda/utils/
// stripePriceCatalog.ts) — see this file's header. Cheapest first when
// there is more than one.
const SELF_SERVE_TIERS: readonly BillingTier[] = ["top"];
const INTERVALS: readonly BillingInterval[] = ["monthly", "annual"];
// A lone plan is pre-selected; a real choice starts unselected (see header).
const INITIAL_TIER: BillingTier | "" = SELF_SERVE_TIERS.length === 1 ? SELF_SERVE_TIERS[0] : "";


interface RegisterErrorDetails {
  message: string;
  emailAlreadyRegistered: boolean;
}

// Isolates the one instanceof/err.code check this page needs from the
// component body, so both the message AND the "show a /login pointer" flag
// come from a single, testable place — see this file's header for the exact
// backend contract this mirrors.
function registerErrorDetails(err: unknown): RegisterErrorDetails {
  if (err instanceof ApiError && err.code === "EMAIL_ALREADY_REGISTERED") {
    return { message: err.message, emailAlreadyRegistered: true };
  }
  return { message: friendlyErrorMessage(err, "creating your account"), emailAlreadyRegistered: false };
}

export function SignupPage() {

  const [tier, setTier] = useState<BillingTier | "">(INITIAL_TIER);
  // Not named setInterval — that would shadow the global window.setInterval.
  const [interval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showLoginPointer, setShowLoginPointer] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setShowLoginPointer(false);

    const trimmedBusinessName = businessName.trim();
    const trimmedContactName = contactName.trim();
    // Lowercased to match LoginPage.tsx's own normalization before it calls
    // Cognito (`email.trim().toLowerCase()`) — this is what registration
    // actually sets as the Cognito username, so keeping the two consistent
    // is what lets a builder log in later with whatever case they happen to
    // type, matching how they'll normally type it.
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedBusinessName) {
      setSubmitError("Business name is required.");
      return;
    }
    if (trimmedBusinessName.length > MAX_BUSINESS_NAME_LENGTH) {
      setSubmitError(`Business name must be at most ${MAX_BUSINESS_NAME_LENGTH} characters.`);
      return;
    }
    if (!trimmedContactName) {
      setSubmitError("Your name is required.");
      return;
    }
    if (trimmedContactName.length > MAX_CONTACT_NAME_LENGTH) {
      setSubmitError(`Your name must be at most ${MAX_CONTACT_NAME_LENGTH} characters.`);
      return;
    }
    if (!trimmedEmail || !EMAIL_RX.test(trimmedEmail)) {
      setSubmitError("Enter a valid email address.");
      return;
    }
    if (!tier) {
      setSubmitError("Choose a plan to continue.");
      return;
    }
    if (!consentChecked) {
      setSubmitError("You must agree to the Terms, Privacy Policy, and Cookie Policy to continue.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await register({
        businessName: trimmedBusinessName,
        contactName: trimmedContactName,
        email: trimmedEmail,
        tier,
        interval,
        consent: { policyVersion: CONSENT_POLICY_VERSION, textShown: CONSENT_TEXT_SHOWN },
      });
      // Hand off to Stripe. assign(), not replace(): Back should return
      // here rather than skipping over signup entirely, so a visitor who
      // bails out of Checkout lands somewhere that explains itself.
      // Nothing is set on state afterwards — this navigates away.
      window.location.assign(result.checkoutUrl);
      return;
    } catch (err) {
      const details = registerErrorDetails(err);
      setSubmitError(details.message);
      setShowLoginPointer(details.emailAlreadyRegistered);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className={styles.pageContainer}>
      <div className={styles.card}>
        <BrandHeader />

        {(
          <form onSubmit={handleSubmit} className={styles.form}>
            <h1>Start your free trial</h1>
            <p className={styles.subtle}>
              30 days free. Card required — you won&rsquo;t be charged until the trial ends,
              and you can cancel any time before then.
            </p>

            <fieldset className={styles.planFieldset}>
              <legend className={styles.planLegend}>Billing interval</legend>
              <div className={styles.intervalOptions}>
                {INTERVALS.map((iv) => (
                  <label key={iv} className={styles.intervalOption}>
                    <input
                      type="radio"
                      name="interval"
                      value={iv}
                      checked={interval === iv}
                      onChange={() => setBillingInterval(iv)}
                    />
                    <span>{intervalLabel(iv)}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className={styles.planFieldset}>
              <legend className={styles.planLegend}>
                {SELF_SERVE_TIERS.length === 1 ? "Your plan" : "Choose your plan"}
              </legend>
              <div className={styles.tierOptions}>
                {SELF_SERVE_TIERS.map((t) => (
                  <label className={styles.tierOption} key={t}>
                    <input
                      type="radio"
                      name="tier"
                      value={t}
                      checked={tier === t}
                      onChange={() => setTier(t)}
                    />
                    <span className={styles.tierOptionBody}>
                      <span className={styles.tierOptionLabel}>{tierLabel(t)}</span>
                      <span className={styles.tierOptionDesc}>{TIER_BLURBS[t]}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className={styles.field}>
              <span>Business name</span>
              <input
                type="text" required maxLength={MAX_BUSINESS_NAME_LENGTH}
                value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                autoComplete="organization" autoFocus
              />
            </label>
            <label className={styles.field}>
              <span>Your name</span>
              <input
                type="text" required maxLength={MAX_CONTACT_NAME_LENGTH}
                value={contactName} onChange={(e) => setContactName(e.target.value)}
                autoComplete="name"
              />
            </label>
            <label className={styles.field}>
              <span>Email</span>
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>

            <label className={`${styles.field} ${styles.consentField}`}>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span>
                I agree to the{" "}
                <a href={CONSENT_DOCUMENTS.terms.url} target="_blank" rel="noopener noreferrer">
                  {CONSENT_DOCUMENTS.terms.label}
                </a>
                {", "}
                <a href={CONSENT_DOCUMENTS.privacy.url} target="_blank" rel="noopener noreferrer">
                  {CONSENT_DOCUMENTS.privacy.label}
                </a>
                {", and "}
                <a href={CONSENT_DOCUMENTS.cookie.url} target="_blank" rel="noopener noreferrer">
                  {CONSENT_DOCUMENTS.cookie.label}
                </a>
                .
              </span>
            </label>

            {submitError && (
              <StatusMessage variant="error" message={submitError} className={styles.error}>
                {showLoginPointer && (
                  <>
                    {" "}
                    <Link to="/login" className={styles.textLink}>Go to login</Link>
                  </>
                )}
              </StatusMessage>
            )}

            <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
              {isSubmitting ? "Opening secure checkout…" : "Continue to payment"}
            </button>

            <p className={styles.cardNote}>
              Your card is handled by Stripe. DockPro never sees or stores your card number.
            </p>

            <div className={styles.linkRow}>
              <span className={styles.subtle}>Already have an account?</span>
              <Link to="/login" className={styles.textLink}>Log in</Link>
            </div>
          </form>
        )}

      </div>
    </main>
  );
}
